import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Quote } from "lucide-react";
import TopBar from "@/components/TopBar";
import Footer from "@/components/landing/sections/Footer";
import Reveal from "@/components/landing/Reveal";
import api from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import JsonLd, { organizationSchema, breadcrumbSchema } from "@/components/JsonLd";
import { renderTitle, toFullUrl } from "@/components/landing/utils";

export default function Nosotros() {
  useSeo({ title: "Quiénes somos · Rubí Perfumería", description: "Rubí Perfumería: empresa mexicana pionera en perfumería de alta gama a precio accesible. +600 esencias, 39 sucursales en 17 estados, 2 CEDIS y un modelo de apertura en menos de 30 días.", path: "/nosotros" });
  const [content, setContent] = useState(null);
  useEffect(() => { api.get("/public/content").then((r) => setContent(r.data)).catch(() => setContent({})); window.scrollTo(0, 0); }, []);
  const a = content?.about;
  if (!content) return <div className="min-h-screen bg-[#FAFAFA]" />;
  if (!a) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-[#52525B]">Contenido no disponible.</div>;
  const hero = toFullUrl(a.hero_image);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A] overflow-x-clip" data-testid="nosotros-page">
      <JsonLd id="org" data={organizationSchema(content.brand)} />
      <JsonLd id="breadcrumb" data={breadcrumbSchema([{ name: "Inicio", path: "/" }, { name: "Quiénes somos", path: "/nosotros" }])} />
      <TopBar variant="public" />
      {/* Hero */}
      <section className="relative bg-[#0A0A0A] on-onyx text-white pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden">
        {hero && <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
        <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-[#FFBF57]" data-testid="nosotros-back"><ArrowLeft size={14} /> Volver</Link>
          <Reveal><span className="eyebrow mt-8 inline-block">{a.eyebrow}</span></Reveal>
          <Reveal delay={120}><h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-4xl">{renderTitle(a.title)}</h1></Reveal>
          <Reveal delay={220}><p className="mt-6 text-[#A1A1AA] text-base md:text-lg leading-relaxed max-w-3xl">{a.intro}</p></Reveal>
          {a.quote && <Reveal delay={300}><div className="mt-8 flex items-start gap-3 text-[#FFBF57] font-display text-xl md:text-2xl max-w-2xl"><Quote size={20} className="shrink-0 mt-1" />{a.quote}</div></Reveal>}
        </div>
      </section>

      {/* Stats */}
      {a.stats?.length > 0 && (
        <section className="bg-[#FAFAFA] py-14 md:py-20 border-b border-black/6">
          <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {a.stats.map((s, i) => (
              <Reveal key={i} delay={i * 80}><div className="border-l border-black/10 pl-4"><div className="font-display text-3xl md:text-4xl num-mono">{s.n}</div><div className="mt-2 text-[10px] uppercase tracking-widest font-mono text-[#52525B]">{s.l}</div></div></Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Pillars */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-start">
            <div className="lg:sticky lg:top-28">
              <Reveal><span className="eyebrow">Somos más que una perfumería</span></Reveal>
              <Reveal delay={120}><h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02]">Lo que nos hace <em className="accent not-italic">diferentes</em>.</h2></Reveal>
              <Reveal delay={220}>
                <div className="mt-8 rounded-3xl overflow-hidden aspect-[4/4.4] sm:aspect-[4/5] bg-[#0A0A0A] relative" data-testid="nosotros-bottles">
                  <div className="absolute inset-0 grid-lines opacity-20" />
                  <motion.div className="absolute inset-x-6 bottom-6 h-16 rounded-full bg-[#FFBF57]/30 blur-2xl" animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.85, 0.5] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />
                  <motion.img src="/perfumes/dama-negro.png" alt="Perfume Rubí negro" loading="lazy" decoding="async"
                    className="absolute left-[-18%] bottom-[2%] w-[92%] object-contain drop-shadow-[0_30px_50px_rgba(0,0,0,0.7)]"
                    animate={{ y: [0, -14, 0], rotate: [-6, -3, -6] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }} />
                  <motion.img src="/perfumes/dama-blanco.png" alt="Perfume Rubí blanco" loading="lazy" decoding="async"
                    className="absolute right-[-18%] bottom-[8%] w-[92%] object-contain drop-shadow-[0_30px_50px_rgba(0,0,0,0.7)]"
                    animate={{ y: [0, -18, 0], rotate: [5, 2, 5] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 0.6 }} />
                  <div className="absolute bottom-4 left-4 pill pill-gold !text-[9px]">Perfume concentrado · 30 ml</div>
                </div>
              </Reveal>
            </div>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="nosotros-pillars">
              {(a.pillars || []).map((p, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                  className="rounded-3xl bg-white border border-black/6 p-6 hover:border-[#FFBF57]/60 transition-colors group">
                  <div className="font-mono text-[11px] tracking-[0.2em] text-[#A1A1AA] group-hover:text-[#FFBF57] transition-colors">0{i + 1}</div>
                  <div className="mt-4 font-display text-xl">{p.title}</div>
                  <p className="mt-2 text-sm text-[#3F3F46] leading-relaxed">{p.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Expansion */}
      <section className="bg-[#0A0A0A] on-onyx text-white py-20 md:py-28">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Reveal><span className="eyebrow">Plan de expansión</span></Reveal>
            <Reveal delay={120}><h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02]">{a.expansion_title}</h2></Reveal>
            <Reveal delay={220}><p className="mt-6 text-[#A1A1AA] leading-relaxed">{a.expansion_body}</p></Reveal>
            <Reveal delay={300}>
              <Link to="/#ubicaciones" className="btn-outline !border-white/20 !text-white mt-8 inline-flex" data-testid="nosotros-map-link">Ver mapa de sucursales <ArrowUpRight size={14} /></Link>
            </Reveal>
          </div>
          {toFullUrl(a.team_image) && <Reveal delay={200} scale><div className="rounded-3xl overflow-hidden aspect-[4/3]"><img src={toFullUrl(a.team_image)} alt="Equipo Rubí" loading="lazy" className="w-full h-full object-cover" /></div></Reveal>}
        </div>
      </section>

      {/* Value proposition */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10">
          <Reveal><span className="eyebrow">{a.value_title || "Propuesta de valor"}</span></Reveal>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {(a.value_items || []).map((v, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className={`rounded-3xl p-6 md:p-8 h-full ${i === 0 ? "bg-[#FFBF57] text-[#0A0A0A]" : "bg-white border border-black/6"}`}>
                  <Check size={18} className={i === 0 ? "text-[#0A0A0A]" : "text-[#FFBF57]"} strokeWidth={2.5} />
                  <div className="mt-4 font-display text-2xl">{v.title}</div>
                  <p className={`mt-3 text-sm leading-relaxed ${i === 0 ? "text-[#0A0A0A]/80" : "text-[#3F3F46]"}`}>{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {a.gallery?.length > 0 && (
        <section className="pb-20 md:pb-28">
          <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10">
            <Reveal><span className="eyebrow">Nuestras tiendas e inauguraciones</span></Reveal>
            <div className="mt-8 columns-2 md:columns-3 lg:columns-4 gap-3 [&>*]:mb-3" data-testid="nosotros-gallery">
              {a.gallery.map((g, i) => (
                <div key={i} className="break-inside-avoid rounded-2xl overflow-hidden bg-[#E4E4E7] relative group">
                  <img src={toFullUrl(g.image_url)} alt={g.title || ""} loading="lazy" decoding="async" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700" />
                  {g.title && <div className="absolute bottom-2 left-2 text-[9px] uppercase tracking-widest font-mono bg-[#0A0A0A]/70 text-[#FFBF57] px-2 py-1 rounded-full">{g.title}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#0A0A0A] on-onyx text-white py-16">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="font-display text-2xl md:text-4xl max-w-2xl">Participa en la próxima etapa de expansión de Rubí.</div>
          <Link to="/#proyectos" className="btn-primary !bg-[#FFBF57] !text-[#0A0A0A] justify-center" data-testid="nosotros-cta">Ver Proyecto Diamante <ArrowUpRight size={16} /></Link>
        </div>
      </section>
      <Footer brand={content.brand} />
    </div>
  );
}
